import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AppLink, useGo } from '../components/Nav';
import { ArrowLeftIcon, PlusIcon, ScissorsIcon, TagIcon, TrashIcon, XIcon } from '../components/icons';
import { Banner, Button, ErrorText, Loading, Spinner, cx } from '../components/ui';
import { track } from '../lib/amplitude';
import { paywallPath } from '../lib/plans';
import { ApiError, errorMessage } from '../lib/api';
import { timecode } from '../lib/format';
import { loadWaveform } from '../player/waveform';
import { usePlanPlayer } from '../player/usePlanPlayer';
import type { SharePayload } from '../lib/types';
import {
  applyEdit,
  fetchClipSource,
  fetchEditState,
  type Clip,
  type ClipSource,
  type EditState,
  type Piece,
  type Range,
  type Word,
} from '../queries/edit';
import { qk } from '../queries/keys';
import {
  MAX_CLIPS,
  MIN_PIECE_SEC,
  chaptersOf,
  clipCount,
  forApi,
  isWhole,
  movePiece,
  newId,
  pieceId,
  pieceLength,
  piecesOf,
  plannedDuration,
  removePiece,
  retitlePiece,
  selfRanges,
  splitPiece,
  subtractRange,
  trimEdge,
  type Placed,
} from '../lib/timeline';
import { waveformEnvelope } from '../lib/waveform';
import { ClipPicker } from './EditClips';

/**
 * The editor.
 *
 * One timeline: the finished video, piece by piece, in the order it plays.
 * A piece is a range of the original recording or a range of another
 * recording dropped in as a clip, and every piece is handled the same way:
 * split it at the playhead, delete it, drag it to another position, drag
 * its edges to trim. The video above plays the pieces in that order, and
 * the transcript on the right says what the finished video says, piece by
 * piece in that same order: nothing that was cut is shown.
 *
 * The waveform behind the pieces and the transcript both belong to the
 * original, and each piece of the original draws its own slice of them;
 * a clip draws its poster instead. "Finish" sends the list to the API,
 * which renders it and regenerates the captions. Nothing is destructive
 * until then, and even then the original is kept, as is every recording a
 * clip was taken from.
 */

/** Everything an undo has to put back. */
interface Snapshot {
  pieces: Piece[];
  words: Word[];
}

/** The pieces as the older API shape, sent alongside for a build that predates ordered pieces. */
function legacyOf(pieces: Piece[], duration: number): { keep: Range[] | null; clips: Clip[] } {
  const keep = isWhole(pieces, duration) ? null : selfRanges(pieces);
  const clips: Clip[] = [];
  pieces.forEach((piece, index) => {
    if (piece.kind !== 'clip') return;
    const after = pieces.slice(index + 1).find((one) => one.kind === 'self');
    clips.push({ id: piece.id, videoId: piece.videoId, at: after ? after.start : duration, start: piece.start, end: piece.end });
  });
  return { keep, clips };
}

export function EditPage({ videoId }: { videoId: string }) {
  const go = useGo();
  const state = useQuery({
    queryKey: ['edit', videoId],
    queryFn: () => fetchEditState(videoId),
    retry: 1,
  });

  if (state.isPending) return <Loading label="Opening the editor" />;
  if (state.isError) {
    const upsell = state.error instanceof ApiError && state.error.isQuota;
    return (
      <div className="mx-auto max-w-md py-16">
        {upsell ? (
          <Banner
            tone="info"
            title="Editing comes with a paid seat"
            action={
              <AppLink to={paywallPath('editing')}>
                <Button size="sm" variant="primary">
                  Upgrade
                </Button>
              </AppLink>
            }
          >
            Cut, trim and correct the transcript of anything you record, on the Team plan. The
            recording itself is untouched and still yours.
          </Banner>
        ) : (
          <Banner tone="danger" title="Could not open this recording for editing">
            {errorMessage(state.error)}
          </Banner>
        )}
      </div>
    );
  }
  const data = state.data;
  if (data.status !== 'ready') {
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <Spinner size={22} className="mx-auto text-chalk-faint" />
        <h1 className="mt-4 text-h3 text-chalk">Still processing</h1>
        <p className="mt-2 text-body text-chalk-dim">
          Editing opens up once the recording is ready. This usually takes a minute or two.
        </p>
        <AppLink to={`/v/${data.shareId}`} className="mt-5 inline-block">
          <Button>Back to the recording</Button>
        </AppLink>
      </div>
    );
  }
  return <Editor initial={data} onDone={() => go(`/v/${data.shareId}`)} />;
}

function Editor({ initial, onDone }: { initial: EditState; onDone: () => void }) {
  const duration = initial.sourceDurationSec;

  useEffect(() => {
    track('Edit Started', { video_id: initial.shareId });
  }, [initial.shareId]);

  // ---- state and history ---------------------------------------------------
  const [initialPieces] = useState<Piece[]>(() => piecesOf(initial, duration));
  const [pieces, setPieces] = useState<Piece[]>(initialPieces);
  const [words, setWords] = useState<Word[]>(initial.words ?? []);
  const [history, setHistory] = useState<Snapshot[]>([]);
  const [future, setFuture] = useState<Snapshot[]>([]);
  const dragBaseline = useRef<Snapshot | null>(null);
  const here = (): Snapshot => ({ pieces, words });
  const commit = useCallback(
    (next: { pieces?: Piece[]; words?: Word[] }) => {
      // An explicit action owns its history entry; a drag in flight does not
      // get to add another at mouse-up.
      dragBaseline.current = null;
      setHistory((h) => [...h, { pieces, words }].slice(-100));
      setFuture([]);
      if (next.pieces) setPieces(next.pieces);
      if (next.words) setWords(next.words);
    },
    [pieces, words],
  );
  const restoreSnapshot = (snapshot: Snapshot) => {
    setPieces(snapshot.pieces);
    setWords(snapshot.words);
  };
  const undo = () => {
    const last = history[history.length - 1];
    if (!last) return;
    setFuture((f) => [here(), ...f]);
    setHistory((h) => h.slice(0, -1));
    restoreSnapshot(last);
  };
  const redo = () => {
    const next = future[0];
    if (!next) return;
    setHistory((h) => [...h, here()]);
    setFuture((f) => f.slice(1));
    restoreSnapshot(next);
  };
  /**
   * A drag: the edit changes on every move, and the release is one undo.
   * `dragging` is on for its length, so the parts of the page that need not
   * follow every pixel (the transcript) wait for the release.
   */
  const [dragging, setDragging] = useState(false);
  const dragPieces = (next: Piece[]) => {
    if (!dragBaseline.current) {
      dragBaseline.current = { pieces, words };
      setDragging(true);
    }
    setPieces(next);
  };
  useEffect(() => {
    const up = () => {
      const base = dragBaseline.current;
      dragBaseline.current = null;
      setDragging(false);
      if (!base) return;
      if (JSON.stringify(base.pieces) !== JSON.stringify(pieces)) {
        setHistory((h) => [...h, base].slice(-100));
        setFuture([]);
      }
    };
    // Losing the window mid-drag is the end of the drag too; a release the
    // page never saw would otherwise leave it waiting.
    window.addEventListener('mouseup', up);
    window.addEventListener('blur', up);
    return () => {
      window.removeEventListener('mouseup', up);
      window.removeEventListener('blur', up);
    };
  }, [pieces]);
  const wordsChanged = JSON.stringify(words) !== JSON.stringify(initial.words ?? []);
  const dirty = JSON.stringify(forApi(pieces)) !== JSON.stringify(forApi(initialPieces)) || wordsChanged;

  // ---- the clips' recordings -----------------------------------------------
  /**
   * The sources start as whatever the API sent with the edit, and grow as
   * clips are added. Kept past a removal on purpose: an undo brings the clip
   * back and there is no reason to fetch its poster a second time.
   */
  const [sources, setSources] = useState<Map<string, ClipSource>>(
    () => new Map((initial.clipSources ?? []).map((source) => [source.videoId, source])),
  );
  const [picking, setPicking] = useState(false);
  const [pickError, setPickError] = useState<string | null>(null);
  const lengthOf = (piece: Piece) => (piece.kind === 'clip' ? (sources.get(piece.videoId)?.durationSec ?? null) : duration);

  // ---- playback -----------------------------------------------------------------
  const player = usePlanPlayer({ pieces });
  const { plan, total, outTime, sourceTime, playing, current, activeClip, clipTime, toggle, seekOut } = player;
  // The transcript is hundreds of words; it follows the release of a drag, not every move of it.
  const settledPlanRef = useRef(plan);
  if (!dragging) settledPlanRef.current = plan;
  const settledPlan = settledPlanRef.current;
  const [videoReady, setVideoReady] = useState(false);

  // ---- what the hand is on ------------------------------------------------------
  /**
   * The piece being worked on is the one under the playhead; nothing is
   * ever "selected". A piece's own controls appear on it under the hand. A
   * range dragged out in the transcript is a selection of the original
   * instead, and Delete takes that out of whichever pieces play it.
   */
  const target: Placed | null = current;
  const [selection, setSelection] = useState<Range | null>(null);
  /** The piece whose chapter name is being edited, and where on screen it sits. */
  const [namingAt, setNamingAt] = useState<{ id: string; x: number; y: number; width: number } | null>(null);
  const naming = namingAt ? (plan.find((one) => pieceId(one.piece) === namingAt.id) ?? null) : null;

  const addClip = async (videoId: string) => {
    setPicking(false);
    setPickError(null);
    try {
      const source = await fetchClipSource(videoId);
      if (!source.available || !source.durationSec) {
        setPickError(`“${source.title}” cannot be played, so it cannot be dropped in.`);
        return;
      }
      setSources((map) => new Map(map).set(source.videoId, source));
      // Onto the end of the timeline, where the button sits; drag it from there.
      const clip: Piece = {
        kind: 'clip',
        id: newId('c'),
        videoId: source.videoId,
        start: 0,
        end: Math.min(source.durationSec, 30),
      };
      commit({ pieces: [...pieces, clip] });
      seekOut(plannedDuration(plan));
      track('Clip Added', { video_id: initial.shareId, clip_video_id: source.videoId });
    } catch (error) {
      setPickError(errorMessage(error));
    }
  };

  // ---- actions ---------------------------------------------------------------
  /** Where a split would fall: a second inside the current piece's source. */
  const splitAt = current ? (current.piece.kind === 'clip' ? clipTime : sourceTime) : null;
  const canSplit =
    current !== null &&
    splitAt !== null &&
    splitAt > current.piece.start + MIN_PIECE_SEC &&
    splitAt < current.piece.end - MIN_PIECE_SEC;
  const deletable = selection !== null ? subtractRange(pieces, selection) : target ? removePiece(pieces, target.index) : null;
  const canDelete = deletable !== null;

  const splitHere = () => {
    if (!current || splitAt === null || !canSplit) return;
    commit({ pieces: splitPiece(pieces, current.index, splitAt) });
    setSelection(null);
  };
  const deleteTarget = () => {
    if (!deletable) return;
    commit({ pieces: deletable });
    setSelection(null);
  };
  /** Whether a piece may go: the row has to keep something of the original. */
  const canRemove = (id: string) => {
    const placed = plan.find((one) => pieceId(one.piece) === id);
    return placed !== undefined && removePiece(pieces, placed.index) !== null;
  };
  const removeById = (id: string) => {
    const placed = plan.find((one) => pieceId(one.piece) === id);
    if (!placed) return;
    const next = removePiece(pieces, placed.index);
    if (!next) return;
    commit({ pieces: next });
    setSelection(null);
  };
  const trim = (index: number, edge: 'start' | 'end', to: number) => {
    if (!pieces[index]) return;
    dragPieces(trimEdge(pieces, index, edge, to, duration, lengthOf));
  };
  // A move lands on release, as one action; the timeline shows it in flight.
  const move = (from: number, to: number) => commit({ pieces: movePiece(pieces, from, to) });

  // Keyboard: space plays, S splits, delete removes the piece or the
  // selection, cmd/ctrl-z undoes.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const el = event.target as HTMLElement | null;
      if (el && (el.isContentEditable || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) return;
      if (event.key === ' ') {
        event.preventDefault();
        toggle();
      } else if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault();
        deleteTarget();
      } else if (event.key.toLowerCase() === 's' && !event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        splitHere();
      } else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
      } else if (event.key === 'Escape') {
        setSelection(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // ---- finish ------------------------------------------------------------------
  const queryClient = useQueryClient();
  const finish = useMutation({
    mutationFn: () =>
      applyEdit(initial.videoId, {
        pieces: forApi(pieces),
        // The older shape rides along for an API build that predates pieces.
        ...legacyOf(pieces, duration),
        // Only words that were touched. A recording with no transcript has
        // an empty list here, and sending it would ask for captions of nothing.
        words: wordsChanged ? words : undefined,
      }),
    onSuccess: (result) => {
      track('Edit Applied', {
        video_id: initial.shareId,
        pieces: pieces.length,
        clips: clipCount(pieces),
      });
      // The share page plays this edit itself while it renders, from this
      // very state. Written into the cache on the way out so it is there
      // the instant the page comes up, and so the page's first poll does
      // not show the previous version for a beat before the flag lands.
      queryClient.setQueryData<EditState>(['edit', initial.videoId], (old) =>
        old
          ? {
              ...old,
              pieces: forApi(pieces),
              ...legacyOf(pieces, duration),
              words: wordsChanged ? words : old.words,
              version: result.version,
              rendering: result.rendering,
            }
          : old,
      );
      if (result.rendering) {
        queryClient.setQueriesData<SharePayload>({ queryKey: qk.share(initial.shareId) }, (old) =>
          old ? { ...old, rendering: true } : old,
        );
      }
      onDone();
    },
  });

  const activeSource = activeClip && activeClip.piece.kind === 'clip' ? sources.get(activeClip.piece.videoId) : undefined;
  const chapterCount = chaptersOf(plan).length;

  return (
    <div className="flex h-screen flex-col bg-slate-950 text-chalk">
      <header className="shrink-0 border-b border-hairline">
        <div className="flex h-12 w-full items-center gap-3 px-4">
          <AppLink to={`/v/${initial.shareId}`} aria-label="Back" className="text-chalk-dim hover:text-chalk">
            <ArrowLeftIcon size={18} />
          </AppLink>
          <h1 className="min-w-0 flex-1 truncate text-body text-chalk">{initial.title}</h1>
          <span className="hidden text-caption text-chalk-faint sm:inline">
            {dirty ? 'Unsaved changes' : 'No changes'} · will run {timecode(total)}
            {chapterCount > 0 ? ` · ${chapterCount} ${chapterCount === 1 ? 'chapter' : 'chapters'}` : ''}
          </span>
          <button
            type="button"
            onClick={undo}
            disabled={history.length === 0}
            className="text-caption text-chalk-dim disabled:opacity-40 hover:text-chalk"
          >
            Undo
          </button>
          <button
            type="button"
            onClick={redo}
            disabled={future.length === 0}
            className="text-caption text-chalk-dim disabled:opacity-40 hover:text-chalk"
          >
            Redo
          </button>
          <Button variant="primary" size="sm" busy={finish.isPending} disabled={!dirty} onClick={() => finish.mutate()}>
            Finish
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 w-full flex-1 flex-col px-4">
        {finish.isError ? (
          <div className="pt-3">
            <ErrorText>Could not save this edit. {errorMessage(finish.error)}</ErrorText>
          </div>
        ) : null}

        <div className="flex min-h-0 flex-1 gap-4 pt-4">
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-md border border-hairline bg-black" data-theme="dark">
              <video
                ref={player.videoRef}
                src={initial.sourceUrl}
                className="max-h-full max-w-full"
                onClick={toggle}
                onCanPlay={() => setVideoReady(true)}
                playsInline
                preload="auto"
              />
              {activeClip && activeClip.piece.kind === 'clip' ? (
                <div className="absolute inset-0 flex items-center justify-center bg-black">
                  <video
                    ref={player.clipVideoRef}
                    key={pieceId(activeClip.piece)}
                    src={activeSource?.url ?? undefined}
                    className="max-h-full max-w-full"
                    onClick={toggle}
                    playsInline
                    preload="auto"
                    {...player.clipHandlers}
                  />
                  <div className="pointer-events-none absolute left-3 top-3 rounded bg-slate-950/80 px-2 py-1 text-caption text-chalk">
                    {activeSource?.title ?? 'Clip'}
                  </div>
                </div>
              ) : null}
            </div>
            <div className="flex h-10 shrink-0 items-center gap-3 px-1 text-caption">
              <button type="button" onClick={toggle} className="text-chalk hover:text-chalk">
                {playing ? 'Pause' : 'Play'}
              </button>
              <span className="font-mono tnum text-chalk-dim">
                {timecode(outTime)} / {timecode(total)}
              </span>
              {current?.piece.kind === 'self' ? (
                <span className="font-mono tnum text-chalk-faint">· {timecode(sourceTime)} of the original</span>
              ) : activeSource ? (
                <span className="truncate text-chalk-faint">· clip from {activeSource.title}</span>
              ) : null}
              <span className="flex-1" />
              <span className="hidden text-chalk-faint lg:inline">Space plays · S splits · ⌫ deletes · ⌘Z undoes</span>
            </div>
          </div>

          <aside className="w-[380px] shrink-0 overflow-y-auto rounded-md border border-hairline px-4 py-4">
            <TranscriptEditor
              plan={settledPlan}
              words={words}
              sources={sources}
              outTime={outTime}
              currentId={current ? pieceId(current.piece) : null}
              status={initial.transcriptStatus}
              selection={selection}
              onSeekOut={seekOut}
              onSelect={setSelection}
              onChangeWord={(index, text) => commit({ words: words.map((w, i) => (i === index ? { ...w, w: text } : w)) })}
              onReplaceSpan={(from, to, replacement) =>
                commit({ words: [...words.slice(0, from), ...replacement, ...words.slice(to)] })
              }
            />
          </aside>
        </div>

        <Timeline
          plan={plan}
          total={total}
          duration={duration}
          // The raw upload streams as fragments and is read first; the
          // render is the fallback once the raw has expired.
          waveformUrls={[initial.waveformUrl, initial.sourceUrl].filter((url): url is string => Boolean(url))}
          sources={sources}
          outTime={outTime}
          currentId={current ? pieceId(current.piece) : null}
          waveformReady={videoReady}
          onSeekOut={(out) => {
            setSelection(null);
            seekOut(out);
          }}
          onMove={move}
          onTrim={trim}
          onRemove={removeById}
          canRemove={canRemove}
          onName={(id, box) => setNamingAt({ id, x: box.left, y: box.top, width: box.width })}
          // Nothing to stitch into a voice note: every clip is a picture.
          onAdd={initial.kind === 'audio' ? null : () => setPicking(true)}
          addDisabled={clipCount(pieces) >= MAX_CLIPS}
          tools={
            <EditTools
              selection={selection}
              canSplit={canSplit}
              canDelete={canDelete}
              onSplit={splitHere}
              onDelete={deleteTarget}
              onClear={() => setSelection(null)}
            />
          }
        >
          {pickError ? <ErrorText>{pickError}</ErrorText> : null}
        </Timeline>
      </div>

      {picking ? (
        <ClipPicker excludeVideoId={initial.videoId} onPick={(video) => void addClip(video.id)} onClose={() => setPicking(false)} />
      ) : null}

      {naming && namingAt ? (
        <ChapterPopover
          key={pieceId(naming.piece)}
          title={naming.piece.title ?? ''}
          x={namingAt.x}
          y={namingAt.y}
          width={namingAt.width}
          onSave={(title) => {
            if (title.trim() !== (naming.piece.title ?? '').trim()) commit({ pieces: retitlePiece(pieces, naming.index, title) });
            setNamingAt(null);
          }}
          onClose={() => setNamingAt(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * The controls for what the hand is on, over the timeline where the hand
 * already is. What they read depends on it: a range dragged out in the
 * transcript deletes as a range, a piece as a piece.
 */
function EditTools({
  selection,
  canSplit,
  canDelete,
  onSplit,
  onDelete,
  onClear,
}: {
  /** A range of the original dragged out in the transcript. */
  selection: Range | null;
  canSplit: boolean;
  canDelete: boolean;
  onSplit: () => void;
  onDelete: () => void;
  onClear: () => void;
}) {
  const span = (range: Range) => `${timecode(range[0])} to ${timecode(range[1])}`;
  return (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        onClick={onSplit}
        disabled={!canSplit}
        title={canSplit ? 'Split the piece at the playhead (S)' : 'Move the playhead inside a piece to split it'}
      >
        <ScissorsIcon size={13} />
        Split
      </Button>
      {/* A piece is deleted from the piece itself; a range dragged out in
          the transcript has no piece to hold the button, so it is here. */}
      {selection ? (
        <>
          <Button size="sm" variant="danger" onClick={onDelete} disabled={!canDelete} title="Take the selected range out (Delete)">
            <TrashIcon size={13} />
            Delete {span(selection)}
          </Button>
          <button type="button" onClick={onClear} className="text-caption text-chalk-faint hover:text-chalk">
            Clear selection
          </button>
        </>
      ) : null}
    </div>
  );
}

/**
 * Naming the chapter a piece begins: a small field hung over the piece
 * itself, where the hand already is. A name makes the piece the start of a
 * chapter that runs until the next named piece; the player shows the name
 * as it plays and lists every chapter for jumping to. Enter or clicking
 * away keeps what was typed; Escape drops it; the cross takes the chapter
 * away.
 */
function ChapterPopover({
  title,
  x,
  y,
  width,
  onSave,
  onClose,
}: {
  title: string;
  /** The piece's box on screen: the field sits just above its left edge. */
  x: number;
  y: number;
  width: number;
  onSave: (title: string) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(title);
  const boxRef = useRef<HTMLDivElement>(null);
  // Save on the way out, however the person leaves; the field is the only
  // thing here, so leaving it is the end of the naming.
  useEffect(() => {
    const away = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) onSave(draft);
    };
    const gone = () => onSave(draft);
    window.addEventListener('mousedown', away);
    window.addEventListener('resize', gone);
    window.addEventListener('blur', gone);
    return () => {
      window.removeEventListener('mousedown', away);
      window.removeEventListener('resize', gone);
      window.removeEventListener('blur', gone);
    };
  }, [draft, onSave]);
  const fieldWidth = Math.max(220, Math.min(360, width));
  const left = Math.max(8, Math.min(x, window.innerWidth - fieldWidth - 8));
  return (
    <div
      ref={boxRef}
      role="dialog"
      aria-label="Name this chapter"
      className="fixed z-[60] -translate-y-full animate-fade-up"
      style={{ left, top: y - 8, width: fieldWidth }}
    >
      <div className="flex items-center gap-1 rounded-md border border-signal/60 bg-slate-900 p-1 shadow-raised">
        <TagIcon size={13} className="ml-1.5 shrink-0 text-signal" />
        <input
          autoFocus
          value={draft}
          placeholder="Chapter name"
          maxLength={80}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') onSave(draft);
            if (event.key === 'Escape') onClose();
          }}
          className="h-7 min-w-0 flex-1 bg-transparent px-1 text-caption text-chalk placeholder:text-chalk-faint focus:outline-none"
        />
        {title ? (
          <button
            type="button"
            aria-label="Remove the chapter"
            title="Remove the chapter"
            onClick={() => onSave('')}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-chalk-faint transition-colors duration-120 hover:bg-danger hover:text-white"
          >
            <XIcon size={12} />
          </button>
        ) : null}
      </div>
      {/* The point, at the piece. */}
      <div className="ml-4 h-2 w-2 -translate-y-1 rotate-45 border-b border-r border-signal/60 bg-slate-900" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Transcript: what the finished video says, piece by piece, in the order it
// plays. Each piece of the original brings the words inside its range; each
// clip brings its own recording's words. Nothing that was cut is shown,
// because nothing that was cut is said. Click a word to go there; drag
// across words of the original to select a range; double-click a word to
// fix it, or edit a whole line as text: its words are re-spread over the
// line's time, one to one when the count matches and evenly when it does
// not. A clip's words are read, not edited: they belong to the other
// recording.

interface Line {
  from: number; // index of first word
  to: number; // index after last word
}

/** Lines break at sentence ends, pauses, and about a dozen words. */
function linesOf(words: Array<{ s: number; e: number; w: string }>): Line[] {
  const lines: Line[] = [];
  let from = 0;
  for (let i = 0; i < words.length; i += 1) {
    const word = words[i];
    const next = words[i + 1];
    const count = i - from + 1;
    const breakHere = !next || count >= 14 || /[.?!]$/.test(word.w) || next.s - word.e > 1.0;
    if (breakHere) {
      lines.push({ from, to: i + 1 });
      from = i + 1;
    }
  }
  return lines;
}

/** New text for a span of words, keeping the span's timing. */
function retime(old: Word[], text: string): Word[] {
  const tokens = text.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];
  if (tokens.length === old.length) return old.map((word, i) => ({ ...word, w: tokens[i] }));
  const start = old[0].s;
  const end = old[old.length - 1].e;
  const each = (end - start) / tokens.length;
  return tokens.map((w, i) => ({ s: start + i * each, e: start + (i + 1) * each, w }));
}

/** A word as the transcript shows it: on the finished video's clock, with its source word behind it. */
interface Spoken {
  word: Word;
  /** Index into the original's words; -1 for a clip's word. */
  index: number;
  s: number;
  e: number;
  w: string;
}

/** One piece's worth of the transcript. */
interface Passage {
  key: string;
  placed: Placed;
  /** What to call the piece over its words, once there is more than one. */
  label: string;
  /** The original's words may be fixed; a clip's belong to its recording. */
  editable: boolean;
  words: Spoken[];
  lines: Line[];
}

function passagesOf(plan: Placed[], words: Word[], sources: Map<string, ClipSource>): Passage[] {
  return plan.map((placed) => {
    const { piece } = placed;
    const pool = piece.kind === 'self' ? words : (sources.get(piece.videoId)?.words ?? []);
    const spoken: Spoken[] = [];
    pool.forEach((word, index) => {
      if (word.s < piece.start - 0.001 || word.s >= piece.end) return;
      const s = placed.outStart + (word.s - piece.start);
      const e = placed.outStart + Math.min(word.e, piece.end) - piece.start;
      spoken.push({ word, index: piece.kind === 'self' ? index : -1, s, e, w: word.w });
    });
    const where =
      piece.kind === 'self'
        ? `${timecode(piece.start)}–${timecode(piece.end)} of the original`
        : `Clip · ${sources.get(piece.videoId)?.title ?? 'another recording'}`;
    const label = piece.title ? `${piece.title} · ${where}` : where;
    return { key: pieceId(piece), placed, label, editable: piece.kind === 'self', words: spoken, lines: linesOf(spoken) };
  });
}

function TranscriptEditor({
  plan,
  words,
  sources,
  outTime,
  currentId,
  status,
  selection,
  onSeekOut,
  onSelect,
  onChangeWord,
  onReplaceSpan,
}: {
  plan: Placed[];
  words: Word[];
  sources: Map<string, ClipSource>;
  /** A second in the finished video. */
  outTime: number;
  /** The piece under the playhead. */
  currentId: string | null;
  status: 'pending' | 'ready' | 'failed' | null;
  /** A range of the original, dragged out here. */
  selection: Range | null;
  onSeekOut: (out: number) => void;
  onSelect: (range: Range | null) => void;
  onChangeWord: (index: number, text: string) => void;
  onReplaceSpan: (from: number, to: number, replacement: Word[]) => void;
}) {
  const anchor = useRef<{ key: string; at: number } | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const [editingLine, setEditingLine] = useState<string | null>(null);
  const [lineDraft, setLineDraft] = useState('');
  useEffect(() => {
    const clear = () => {
      anchor.current = null;
    };
    window.addEventListener('mouseup', clear);
    return () => window.removeEventListener('mouseup', clear);
  }, []);
  const passages = useMemo(() => passagesOf(plan, words, sources), [plan, words, sources]);
  const anyWords = passages.some((passage) => passage.words.length > 0);

  if (!anyWords) {
    return (
      <div>
        <h2 className="text-h4 text-chalk">Transcript</h2>
        <p className="mt-2 text-body text-chalk-dim">
          {status === 'pending'
            ? 'Still listening. Cuts work now; the words arrive in a minute or two.'
            : 'No transcript for this recording. Cuts still work from the timeline.'}
        </p>
      </div>
    );
  }

  const inSelection = (spoken: Spoken) =>
    spoken.index >= 0 && selection !== null && spoken.word.e > selection[0] && spoken.word.s < selection[1];

  return (
    <div className="select-none">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-h4 text-chalk">Transcript</h2>
        <span className="text-right text-caption text-chalk-faint">
          Click to go there · drag to select · double-click fixes a word
        </span>
      </div>
      <div className="mt-3 space-y-4">
        {passages.map((passage) => {
          const isCurrent = passage.key === currentId;
          return (
            <section key={passage.key}>
              {plan.length > 1 ? (
                <button
                  type="button"
                  onClick={() => onSeekOut(passage.placed.outStart)}
                  className={cx(
                    'mb-1 flex w-full items-center gap-2 rounded px-1 text-left text-micro uppercase tracking-[0.08em]',
                    isCurrent ? 'text-chalk' : 'text-chalk-faint hover:text-chalk-dim',
                  )}
                >
                  <span className="font-mono normal-case tracking-normal">{timecode(passage.placed.outStart)}</span>
                  <span className="truncate">{passage.label}</span>
                  <span className={cx('h-px flex-1', isCurrent ? 'bg-chalk/40' : 'bg-hairline')} />
                </button>
              ) : null}
              {passage.words.length === 0 ? (
                <p className="px-1 text-caption text-chalk-faint">
                  {passage.placed.piece.kind === 'clip' ? 'No transcript for this clip.' : 'Nothing said here.'}
                </p>
              ) : null}
              <div className="space-y-2">
                {passage.lines.map((line, lineIndex) => {
                  const lineKey = `${passage.key}:${lineIndex}`;
                  const lineWords = passage.words.slice(line.from, line.to);
                  const live = outTime >= lineWords[0].s && outTime < lineWords[lineWords.length - 1].e;
                  const first = lineWords[0].index;
                  const after = lineWords[lineWords.length - 1].index + 1;
                  if (editingLine === lineKey) {
                    const save = () => {
                      onReplaceSpan(first, after, retime(lineWords.map((one) => one.word), lineDraft));
                      setEditingLine(null);
                    };
                    return (
                      <div key={lineKey} className="rounded-md border border-chalk/30 bg-slate-900 p-2">
                        <textarea
                          autoFocus
                          value={lineDraft}
                          rows={3}
                          onChange={(event) => setLineDraft(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Escape') setEditingLine(null);
                            if (event.key === 'Enter' && !event.shiftKey) {
                              event.preventDefault();
                              save();
                            }
                          }}
                          className="w-full resize-none bg-transparent text-body text-chalk outline-none"
                        />
                        <div className="mt-1 flex items-center justify-between text-caption text-chalk-faint">
                          <span>
                            {timecode(lineWords[0].s)} to {timecode(lineWords[lineWords.length - 1].e)} · Enter saves, Esc cancels
                          </span>
                          <span>
                            <button type="button" className="hover:text-chalk" onClick={() => setEditingLine(null)}>
                              Cancel
                            </button>
                            <button type="button" className="ml-3 text-chalk hover:underline" onClick={save}>
                              Save
                            </button>
                          </span>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={lineKey} className={cx('group flex gap-2 rounded-md px-1 py-0.5', live && 'bg-slate-900/60')}>
                      <button
                        type="button"
                        onClick={() => onSeekOut(lineWords[0].s)}
                        className="w-11 shrink-0 pt-1 text-left font-mono text-caption text-signal"
                      >
                        {timecode(lineWords[0].s)}
                      </button>
                      <p className="min-w-0 flex-1 leading-7 text-body">
                        {lineWords.map((spoken, offset) => {
                          const at = line.from + offset;
                          const said = outTime >= spoken.s && outTime < spoken.e;
                          const selected = inSelection(spoken);
                          if (passage.editable && editing === spoken.index) {
                            return (
                              <input
                                key={at}
                                autoFocus
                                value={draft}
                                onChange={(event) => setDraft(event.target.value)}
                                onBlur={() => {
                                  if (draft.trim() && draft.trim() !== spoken.w) onChangeWord(spoken.index, draft.trim());
                                  setEditing(null);
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
                                  if (event.key === 'Escape') setEditing(null);
                                }}
                                className="mx-0.5 inline-block w-28 rounded-xs border border-chalk/30 bg-slate-900 px-1 text-body text-chalk"
                              />
                            );
                          }
                          return (
                            <span
                              key={at}
                              onMouseDown={(event) => {
                                if (event.detail === 2) return;
                                anchor.current = passage.editable ? { key: passage.key, at } : null;
                              }}
                              onMouseEnter={() => {
                                const from = anchor.current;
                                // A selection is a range of the original, so it stays inside one piece of it.
                                if (!from || from.key !== passage.key || from.at === at) return;
                                const a = passage.words[Math.min(from.at, at)];
                                const b = passage.words[Math.max(from.at, at)];
                                onSelect([a.word.s, b.word.e]);
                              }}
                              onMouseUp={() => {
                                // A click, not a drag: go there. The piece under the word becomes the current one.
                                const from = anchor.current;
                                if (!from || (from.key === passage.key && from.at === at)) {
                                  onSelect(null);
                                  onSeekOut(spoken.s);
                                }
                                anchor.current = null;
                              }}
                              onDoubleClick={() => {
                                if (!passage.editable) return;
                                setDraft(spoken.w);
                                setEditing(spoken.index);
                              }}
                              className={cx(
                                'cursor-pointer rounded-xs px-0.5 transition-colors',
                                !selected && (said ? 'bg-slate-700 text-chalk' : 'text-chalk-dim hover:text-chalk'),
                                !selected && !said && isCurrent && plan.length > 1 && 'text-chalk',
                                selected && 'bg-record/40 text-chalk',
                              )}
                            >
                              {spoken.w}{' '}
                            </span>
                          );
                        })}
                      </p>
                      {passage.editable ? (
                        <button
                          type="button"
                          aria-label="Edit this line"
                          title="Edit this line"
                          onClick={() => {
                            setLineDraft(lineWords.map((one) => one.w).join(' '));
                            setEditingLine(lineKey);
                          }}
                          className="shrink-0 self-start pt-1 text-caption text-chalk-faint opacity-0 transition-opacity hover:text-chalk group-hover:opacity-100"
                        >
                          ✎
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Timeline: the finished video as a row of pieces, in the order they play.
// Each piece is its own rounded box with a handle at either end, so where
// one ends and the next begins is never in doubt. Click a piece to put the
// playhead there and make it the one being worked on; drag a piece to move
// it, and the others slide aside to show where it will land; drag a handle
// to trim; drag along the ruler to scrub. Zoom with the slider or pinch,
// scroll sideways when zoomed in. The square at the right end adds a clip
// onto the tail.

type TimelineDrag =
  | { kind: 'move'; id: string; grabX: number; grabOffsetPx: number; widthPx: number; moved: boolean }
  | { kind: 'trim'; id: string; edge: 'start' | 'end' }
  | { kind: 'scrub' }
  | null;

/** A move in flight, for drawing: the ghost under the hand and the slot it would drop into. */
interface MoveView {
  id: string;
  /** Left edge of the ghost, in pixels from the track's left. */
  x: number;
  widthPx: number;
  /** Where it would land in the list. */
  to: number;
}

/** The gap between two pieces, so each reads as its own box. */
const GAP_PX = 3;

/**
 * A piece's own waveform, drawn inside it so it moves with the piece. A
 * piece of the original draws its slice of the recording's peaks; a clip
 * has no peaks to read, so it wears the seeded shape its recording wears
 * everywhere else in the app, the same as on its card.
 */
function PieceWave({
  piece,
  peaks,
  duration,
  seed,
  redrawKey,
}: {
  piece: Piece;
  peaks: Float32Array | null;
  duration: number;
  seed: string;
  /** Anything that changes the box's size: zoom, the total, a trim. */
  redrawKey: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    // One draw per frame at most: a handle being dragged changes the box on
    // every mouse move, and the mouse moves faster than the screen.
    const frame = requestAnimationFrame(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(rect.width * devicePixelRatio));
      canvas.height = Math.max(1, Math.floor(rect.height * devicePixelRatio));
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const styles = getComputedStyle(canvas);
      const mid = canvas.height / 2;
      const column = Math.max(1, Math.floor(2 * devicePixelRatio));
      if (piece.kind === 'clip') {
        const signal = styles.getPropertyValue('--c-signal').trim() || '127 178 217';
        ctx.fillStyle = `rgb(${signal} / 0.55)`;
        const bars = Math.max(4, Math.floor(canvas.width / (column * 2)));
        const step = canvas.width / bars;
        waveformEnvelope(seed, bars).forEach((height, i) => {
          const h = Math.max(1, height * canvas.height * 0.7);
          ctx.fillRect(i * step, mid - h / 2, Math.max(1, step - 1), h);
        });
        return;
      }
      if (!peaks) return;
      const ink = styles.getPropertyValue('--c-ink-dim').trim() || '155 167 180';
      ctx.fillStyle = `rgb(${ink} / 0.55)`;
      const secondsPerBucket = duration / peaks.length;
      const seconds = pieceLength(piece);
      for (let x = 0; x < canvas.width; x += column) {
        const t0 = piece.start + (x / canvas.width) * seconds;
        const t1 = piece.start + ((x + column) / canvas.width) * seconds;
        const b0 = Math.max(0, Math.floor(t0 / secondsPerBucket));
        const b1 = Math.min(peaks.length - 1, Math.max(b0, Math.floor(t1 / secondsPerBucket)));
        let peak = 0;
        for (let b = b0; b <= b1; b += 1) if (peaks[b] > peak) peak = peaks[b];
        const h = Math.max(1, peak * canvas.height * 0.9);
        ctx.fillRect(x, mid - h / 2, Math.max(1, column - 0.5), h);
      }
    });
    return () => cancelAnimationFrame(frame);
    // redrawKey is what tells this effect the box changed size.
  }, [peaks, piece, duration, seed, redrawKey]);
  return <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 h-full w-full" />;
}

function Timeline({
  plan,
  total,
  duration,
  waveformUrls,
  sources,
  outTime,
  currentId,
  waveformReady,
  onSeekOut,
  onMove,
  onTrim,
  onRemove,
  canRemove,
  onName,
  onAdd,
  addDisabled,
  tools,
  children,
}: {
  plan: Placed[];
  total: number;
  duration: number;
  /** Where to read the waveform from, in order of preference. */
  waveformUrls: string[];
  sources: Map<string, ClipSource>;
  outTime: number;
  /** The piece under the playhead. */
  currentId: string | null;
  /** The player has its first frames: only then is the waveform worth fetching. */
  waveformReady: boolean;
  onSeekOut: (out: number) => void;
  onMove: (from: number, to: number) => void;
  onTrim: (index: number, edge: 'start' | 'end', to: number) => void;
  /** Take a piece out, from the piece itself. */
  onRemove: (id: string) => void;
  canRemove: (id: string) => boolean;
  /** Name the chapter a piece begins; `box` is where the piece sits on screen, for the field to hang over. */
  onName: (id: string, box: DOMRect) => void;
  /** Null for a recording nothing can be dropped into. */
  onAdd: (() => void) | null;
  addDisabled: boolean;
  /** Split and delete: the editor's own controls, in this panel's header. */
  tools: ReactNode;
  children?: ReactNode;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [peaks, setPeaks] = useState<Float32Array | null>(null);
  const [covered, setCovered] = useState(0);
  const [waveDone, setWaveDone] = useState(false);
  const drag = useRef<TimelineDrag>(null);
  const [moving, setMoving] = useState<MoveView | null>(null);
  const planRef = useRef(plan);
  planRef.current = plan;

  /**
   * The seconds the track's width stands for. Normally the finished video's
   * length, so the row fits. While a handle is being dragged it is frozen
   * at what it was when the drag began: trimming changes the length, and
   * if the scale followed it, every other piece and every mark on the ruler
   * would slide about under the hand. They hold still; the row grows past
   * the right edge or falls short of it until the handle is let go, and
   * then it fits again.
   */
  const [frozen, setFrozen] = useState<number | null>(null);
  const scale = Math.max(frozen ?? total, 0.001);
  /** A second of the finished video, from a point on the track. Past the ends it runs negative or over. */
  const outAt = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    return ((clientX - rect.left) / rect.width) * scale;
  };
  const left = (out: number) => `${(out / scale) * 100}%`;

  /**
   * The waveform, read from the original in the background. It waits for the
   * player to have its first frames: both come from the same file, and the
   * picture matters more than the shape of the sound behind it.
   */
  const waveformKey = waveformUrls.join('\n');
  useEffect(() => {
    if (!waveformReady) return;
    const controller = new AbortController();
    setPeaks(null);
    setCovered(0);
    setWaveDone(false);
    const urls = waveformKey ? waveformKey.split('\n') : [];
    void (async () => {
      // The first source that reads; a raw upload the bucket has let go
      // fails at the fetch and the render is tried instead.
      for (const url of urls) {
        if (controller.signal.aborted) return;
        try {
          await loadWaveform(
            url,
            duration,
            (progress) => {
              setPeaks(new Float32Array(progress.peaks));
              setCovered(progress.coveredSec);
              if (progress.done) setWaveDone(true);
            },
            controller.signal,
          );
          return;
        } catch {
          // Next one.
        }
      }
      setWaveDone(true);
    })();
    return () => controller.abort();
  }, [waveformKey, duration, waveformReady]);

  // Keep the playhead in view when zoomed in and playing.
  useEffect(() => {
    const scroller = scrollRef.current;
    const track = trackRef.current;
    if (!scroller || !track || zoom === 1 || total <= 0) return;
    const x = (outTime / total) * track.clientWidth;
    if (x < scroller.scrollLeft + 40 || x > scroller.scrollLeft + scroller.clientWidth - 40) {
      scroller.scrollLeft = Math.max(0, x - scroller.clientWidth / 3);
    }
  }, [outTime, zoom, total]);

  const ticks = useMemo(() => {
    const visibleSec = scale / zoom;
    const step =
      visibleSec <= 30 ? 1 : visibleSec <= 90 ? 5 : visibleSec <= 300 ? 15 : visibleSec <= 900 ? 60 : visibleSec <= 3600 ? 300 : 600;
    const out: number[] = [];
    for (let t = 0; t <= total; t += step) out.push(t);
    return out;
  }, [scale, total, zoom]);

  /**
   * Where a dragged piece wants to go: the position among the others whose
   * midpoint the cursor has passed. Worked out without the moving piece, so
   * the point under the cursor does not slide about as the piece's own width
   * gets out of the way ahead of it. The list itself changes when the piece
   * is let go; while it is held, the others slide aside to leave its slot
   * open, and a ghost of it follows the hand.
   */
  const dropIndex = (id: string, clientX: number): number => {
    const others = planRef.current.filter((one) => pieceId(one.piece) !== id);
    const out = Math.max(0, Math.min(outAt(clientX), plannedDuration(planRef.current)));
    let cursor = 0;
    for (let i = 0; i < others.length; i += 1) {
      const seconds = others[i].outEnd - others[i].outStart;
      if (out < cursor + seconds / 2) return i;
      cursor += seconds;
    }
    return others.length;
  };

  /**
   * Where each piece sits right now: its place in the finished video, or,
   * while one is held, its place once that one has landed in the open slot.
   * The held piece's own entry is the slot.
   */
  const positions = useMemo(() => {
    const map = new Map<string, number>();
    if (!moving) {
      for (const placed of plan) map.set(pieceId(placed.piece), placed.outStart);
      return map;
    }
    const held = plan.find((one) => pieceId(one.piece) === moving.id);
    const heldLength = held ? held.outEnd - held.outStart : 0;
    const others = plan.filter((one) => pieceId(one.piece) !== moving.id);
    let out = 0;
    others.forEach((placed, i) => {
      if (i === moving.to) {
        if (held) map.set(moving.id, out);
        out += heldLength;
      }
      map.set(pieceId(placed.piece), out);
      out += placed.outEnd - placed.outStart;
    });
    if (held && moving.to >= others.length) map.set(moving.id, out);
    return map;
  }, [plan, moving]);

  useEffect(() => {
    const move = (event: MouseEvent) => {
      const state = drag.current;
      if (!state) return;
      event.preventDefault();
      if (state.kind === 'scrub') {
        onSeekOut(Math.max(0, Math.min(outAt(event.clientX), plannedDuration(planRef.current))));
      } else if (state.kind === 'move') {
        if (!state.moved && Math.abs(event.clientX - state.grabX) < 6) return;
        state.moved = true;
        const rect = trackRef.current?.getBoundingClientRect();
        if (!rect) return;
        setMoving({
          id: state.id,
          x: event.clientX - rect.left - state.grabOffsetPx,
          widthPx: state.widthPx,
          to: dropIndex(state.id, event.clientX),
        });
      } else {
        const placed = planRef.current.find((one) => pieceId(one.piece) === state.id);
        if (!placed) return;
        // A trim handle moves in the finished video's seconds, and a second
        // there is a second inside the source: same scale, no conversion.
        const anchor = state.edge === 'start' ? placed.outStart : placed.outEnd;
        const delta = outAt(event.clientX) - anchor;
        onTrim(placed.index, state.edge, (state.edge === 'start' ? placed.piece.start : placed.piece.end) + delta);
      }
    };
    const up = (event: MouseEvent) => {
      const state = drag.current;
      drag.current = null;
      setFrozen(null);
      if (!state) return;
      if (state.kind !== 'move') return;
      setMoving(null);
      if (!state.moved) {
        // A press with no drag: the playhead goes where the press was.
        onSeekOut(Math.max(0, Math.min(outAt(event.clientX), plannedDuration(planRef.current))));
        return;
      }
      // Let go: the piece lands in the slot the others left open.
      const from = planRef.current.findIndex((one) => pieceId(one.piece) === state.id);
      const to = dropIndex(state.id, event.clientX);
      if (from >= 0 && to !== from) onMove(from, to);
    };
    const lost = () => {
      // The window went away mid-drag: whatever was held is dropped where it was.
      drag.current = null;
      setFrozen(null);
      setMoving(null);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('blur', lost);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('blur', lost);
    };
  });

  const setZoomKeepingCentre = (next: number) => {
    const scroller = scrollRef.current;
    const clamped = Math.max(1, Math.min(40, next));
    if (scroller) {
      const centre = (scroller.scrollLeft + scroller.clientWidth / 2) / (scroller.clientWidth * zoom);
      setZoom(clamped);
      requestAnimationFrame(() => {
        scroller.scrollLeft = centre * scroller.clientWidth * clamped - scroller.clientWidth / 2;
      });
    } else {
      setZoom(clamped);
    }
  };

  const pieceCount = plan.length;
  const clips = plan.filter((one) => one.piece.kind === 'clip').length;
  const hint =
    pieceCount <= 1
      ? 'One piece · put the playhead where the cut goes and press Split, then delete what goes'
      : `${pieceCount} pieces${clips ? `, ${clips} of them clips` : ''} · drag a piece to move it · drag its handles to trim it`;
  const held = moving ? plan.find((one) => pieceId(one.piece) === moving.id) : undefined;
  const heldSource = held?.piece.kind === 'clip' ? sources.get(held.piece.videoId) : undefined;

  /** Which of the two tones a piece of the original wears: they alternate, so neighbours differ. */
  const toneOf = new Map<string, number>();
  plan.forEach((placed) => {
    if (placed.piece.kind === 'self') toneOf.set(pieceId(placed.piece), toneOf.size % 2);
  });

  return (
    <div className="shrink-0 border-t border-hairline pb-4 pt-3">
      <div className="mb-2 flex items-center gap-4 text-caption">
        {tools}
        <span className="flex-1" />
        <span className="hidden truncate text-chalk-faint lg:inline">
          {hint}
          {!waveDone && waveformReady ? <span className="text-chalk-faint/70"> · reading audio{peaks ? ` ${Math.round((covered / duration) * 100)}%` : '…'}</span> : null}
        </span>
        <label className="flex items-center gap-2 text-chalk-faint">
          <button type="button" onClick={() => setZoomKeepingCentre(1)} className={cx('hover:text-chalk', zoom === 1 && 'text-chalk')}>
            Fit
          </button>
          <input
            type="range"
            min={1}
            max={40}
            step={0.5}
            value={zoom}
            onChange={(event) => setZoomKeepingCentre(Number(event.target.value))}
            aria-label="Zoom"
            className="w-28 accent-chalk"
          />
        </label>
      </div>

      <div className="flex items-end gap-2">
        <div
          ref={scrollRef}
          // Fit means fit: the scroller only scrolls once zoomed in.
          className={cx('min-w-0 flex-1 overflow-y-hidden', zoom === 1 ? 'overflow-x-hidden' : 'overflow-x-auto')}
          onWheel={(event) => {
            if (!event.ctrlKey && !event.metaKey) return;
            event.preventDefault();
            setZoomKeepingCentre(zoom * (event.deltaY < 0 ? 1.15 : 1 / 1.15));
          }}
        >
          <div style={{ width: `${zoom * 100}%` }}>
            {/* The ruler: the finished video's seconds. Dragging along it scrubs. */}
            <div
              className="relative h-5 cursor-ew-resize select-none overflow-hidden text-micro text-chalk-faint"
              onMouseDown={(event) => {
                drag.current = { kind: 'scrub' };
                onSeekOut(Math.max(0, Math.min(outAt(event.clientX), total)));
              }}
            >
              {ticks.map((t) => (
                <span key={t} className="absolute -translate-x-1/2 font-mono" style={{ left: left(t) }}>
                  {timecode(t)}
                </span>
              ))}
            </div>

            <div
              ref={trackRef}
              className={cx('relative h-[4.5rem] select-none rounded-md bg-slate-950/40', moving && 'cursor-grabbing')}
            >
              {plan.map((placed) => {
                const { piece } = placed;
                const id = pieceId(piece);
                const seconds = placed.outEnd - placed.outStart;
                const isCurrent = id === currentId;
                const isHeld = moving?.id === id;
                const source = piece.kind === 'clip' ? sources.get(piece.videoId) : undefined;
                const isClip = piece.kind === 'clip';
                const tone = toneOf.get(id) ?? 0;
                const at = positions.get(id) ?? placed.outStart;
                return (
                  <div
                    key={id}
                    className={cx(
                      'group/piece absolute inset-y-1 overflow-hidden rounded-md border',
                      // The slide aside is for a move. A trim moves the neighbours too,
                      // and there they must keep up with the hand, not ease after it.
                      moving && 'transition-[left] duration-200 ease-out',
                      // The slot a held piece will land in: an outline, and nothing inside.
                      isHeld
                        ? 'border-dashed border-chalk/50 bg-transparent'
                        : isClip
                          ? 'border-signal/50 bg-signal/15'
                          : tone === 0
                            ? 'border-chalk/20 bg-slate-800/70'
                            : 'border-chalk/20 bg-slate-850',
                      !isHeld && source && !source.available && 'border-caution/60 bg-caution/20',
                      // Under the playhead, and under the hand: a firmer edge, nothing more.
                      !isHeld && isCurrent && 'border-chalk/40',
                      !isHeld && 'hover:border-chalk/60',
                    )}
                    data-piece={id}
                    style={{
                      left: `calc(${(at / scale) * 100}% + ${GAP_PX / 2}px)`,
                      width: `calc(${(seconds / scale) * 100}% - ${GAP_PX}px)`,
                    }}
                    title={
                      isClip
                        ? `${source?.title ?? 'Clip'} · ${timecode(piece.start)} to ${timecode(piece.end)} of it · drag to move, drag the handles to trim`
                        : `${timecode(piece.start)} to ${timecode(piece.end)} of the original · drag to move, drag the handles to trim`
                    }
                  >
                    {isHeld ? null : (
                      <>
                        <PieceWave
                          piece={piece}
                          peaks={peaks}
                          duration={duration}
                          seed={source?.shareId ?? (isClip ? piece.videoId : 'self')}
                          redrawKey={`${zoom}:${scale}:${piece.start}:${piece.end}`}
                        />
                        <button
                          type="button"
                          aria-label={isClip ? `Move ${source?.title ?? 'clip'}` : `Move the piece from ${timecode(piece.start)}`}
                          onMouseDown={(event) => {
                            event.preventDefault();
                            const box = event.currentTarget.getBoundingClientRect();
                            drag.current = {
                              kind: 'move',
                              id,
                              grabX: event.clientX,
                              grabOffsetPx: event.clientX - box.left,
                              widthPx: box.width,
                              moved: false,
                            };
                          }}
                          className="absolute inset-0 cursor-grab px-3 text-left active:cursor-grabbing"
                          style={
                            source?.posterUrl
                              ? { backgroundImage: `url(${source.posterUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                              : undefined
                          }
                        >
                          {isClip ? <span className="absolute inset-0 bg-slate-950/60" /> : null}
                          {/* A clip says which recording it is from; the chapter name lives on the tag button. */}
                          {isClip ? (
                            <span className="relative block max-w-[45%] truncate pt-1 text-micro text-chalk-dim">
                              {source?.available === false ? 'Unavailable' : (source?.title ?? 'Clip')}
                            </span>
                          ) : null}
                        </button>
                        {/* What can be done to the piece, on the piece, when the hand is over it. */}
                        {moving ? null : (
                          // The piece's own controls. The tag is where the chapter name lives, so
                          // once there is one the tag stays; the rest only ever shows on the piece
                          // under the hand, and never while one is being dragged.
                          <div className="group/actions absolute right-3.5 top-1 z-10 flex max-w-[calc(50%+2rem)] items-center gap-1">
                            <button
                              type="button"
                              aria-label={piece.title ? `Rename the chapter “${piece.title}”` : 'Name this chapter'}
                              title={piece.title ? `Chapter: ${piece.title}` : 'Name this chapter'}
                              onMouseDown={(event) => event.stopPropagation()}
                              onClick={(event) => {
                                const box = (event.currentTarget.closest('[data-piece]') as HTMLElement | null)?.getBoundingClientRect();
                                if (box) onName(id, box);
                              }}
                              className={cx(
                                'flex h-6 min-w-0 items-center gap-1.5 rounded-md bg-signal text-slate-950 shadow-raised transition-[opacity,background-color] duration-120 hover:bg-signal/85',
                                piece.title ? 'px-2 opacity-100' : 'w-6 justify-center opacity-0 focus-visible:opacity-100 group-hover/piece:opacity-100',
                              )}
                            >
                              <TagIcon size={12} className="shrink-0" />
                              {piece.title ? <span className="truncate text-micro font-medium">{piece.title}</span> : null}
                            </button>
                            <button
                              type="button"
                              aria-label={isClip ? `Remove ${source?.title ?? 'this clip'}` : `Remove the piece from ${timecode(piece.start)}`}
                              title={canRemove(id) ? 'Take this piece out' : 'The last piece of the original stays'}
                              disabled={!canRemove(id)}
                              onMouseDown={(event) => event.stopPropagation()}
                              onClick={() => onRemove(id)}
                              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-danger text-white shadow-raised opacity-0 transition-opacity duration-120 focus-visible:opacity-100 group-hover/piece:opacity-100 hover:bg-danger/85 disabled:opacity-0 group-hover/piece:disabled:opacity-40"
                            >
                              <TrashIcon size={12} />
                            </button>
                          </div>
                        )}
                        {/* The handles: always there, brighter on the piece being worked on. */}
                        {(['start', 'end'] as const).map((edge) => (
                          <div
                            key={edge}
                            role="separator"
                            aria-orientation="vertical"
                            aria-label={edge === 'start' ? 'Trim the start' : 'Trim the end'}
                            onMouseDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              // The scale holds until the handle is let go.
                              setFrozen(total);
                              drag.current = { kind: 'trim', id, edge };
                            }}
                            className={cx(
                              'absolute inset-y-0 flex w-2.5 cursor-col-resize items-center justify-center transition-colors duration-120',
                              edge === 'start' ? 'left-0 rounded-l-md' : 'right-0 rounded-r-md',
                              isClip ? 'bg-signal/60 group-hover/piece:bg-signal/80 hover:bg-signal' : 'bg-chalk/30 group-hover/piece:bg-chalk/50 hover:bg-chalk/80',
                            )}
                          >
                            <span className="h-4 w-0.5 rounded-full bg-slate-950/50" />
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                );
              })}
              <div className="pointer-events-none absolute inset-y-0 z-20 w-px bg-chalk" style={{ left: left(outTime) }}>
                <div className="absolute -left-1 -top-0.5 h-2 w-2 rotate-45 bg-chalk" />
              </div>
              {moving && held ? (
                // The piece, under the hand.
                <div
                  className={cx(
                    'pointer-events-none absolute inset-y-0.5 z-30 overflow-hidden rounded-md border shadow-raised',
                    held.piece.kind === 'clip' ? 'border-signal bg-signal/40' : 'border-chalk/80 bg-slate-700',
                  )}
                  style={{ left: moving.x, width: moving.widthPx }}
                >
                  <PieceWave
                    piece={held.piece}
                    peaks={peaks}
                    duration={duration}
                    seed={heldSource?.shareId ?? (held.piece.kind === 'clip' ? held.piece.videoId : 'self')}
                    redrawKey={`ghost:${moving.widthPx}`}
                  />
                  <span className="relative block truncate px-3 pt-1 text-micro font-medium text-chalk">
                    {held.piece.kind === 'clip' ? (heldSource?.title ?? 'Clip') : `${timecode(held.piece.start)}–${timecode(held.piece.end)}`}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {onAdd ? (
          <button
            type="button"
            onClick={onAdd}
            disabled={addDisabled}
            title={addDisabled ? `A recording can hold ${MAX_CLIPS} clips` : 'Add a piece of another recording onto the end'}
            className={cx(
              'flex h-[4.5rem] w-16 shrink-0 flex-col items-center justify-center gap-1 rounded-md border border-dashed border-hairline',
              'text-micro text-chalk-dim transition-colors duration-120 hover:border-chalk/50 hover:bg-slate-900 hover:text-chalk',
              'disabled:opacity-40 disabled:pointer-events-none',
            )}
          >
            <PlusIcon size={16} />
            Add clip
          </button>
        ) : null}
      </div>

      {children ? <div className="mt-2">{children}</div> : null}
    </div>
  );
}
